import dayjs from 'dayjs'
import escape from 'lodash/escape'
import get from 'lodash/get'
import { l } from '@manychat/manyui'

import { CustomAudienceActionType } from 'apps/ads/constants/CustomAudienceType'
import { DelayAutomationsMap } from 'apps/chat/constants/LiveChatThreads'
import { formatMetaTypeCodeToCurrencySign } from 'apps/dashboard/components/Metrics/utils/tickFormatter'
import { IMenu } from 'apps/menu/menuInterfaces'
import { ActiveCampaignConfig } from 'common/actions/integrations/ActiveCampaign/model/constants'
import { ChatGPTConfig } from 'common/actions/integrations/ChatGPT/model/constants'
import { ClaudeConfig } from 'common/actions/integrations/Claude/lib/config'
import { Integration, getIntegrationAction } from 'common/actions/integrations/common/model/helpers'
import { ConvertKitConfig } from 'common/actions/integrations/ConvertKit/model/constants'
import { DeepSeekConfig } from 'common/actions/integrations/DeepSeek/lib/config'
import { GoogleSheetsConfig } from 'common/actions/integrations/GoogleSheets/model/constants'
import { HubSpotConfig } from 'common/actions/integrations/HubSpot/model/constants'
import { KlaviyoConfig } from 'common/actions/integrations/Klaviyo/model/constants'
import { MailChimpConfig } from 'common/actions/integrations/MailChimp/model/constants'
import {
  Action,
  AssignConversationAction,
  CustomEventAction,
  SequenceAction,
  FieldAction,
  SetTagAction,
  ZapAction,
  AppsRequestAction,
  IntegrationAction,
  ExternalRequestAction,
  NotifyAdminAction,
  StartFlowAction,
  SetMainMenuAction,
  CustomAudienceAction,
  PauseAllAutomationsAction,
} from 'common/actions/models/Action/action'
import { getCUFActionLabels, getNotifyAdminsLabels } from 'common/actions/models/Action/formatters'
import UserFieldsTypes from 'common/fields/constants/UserFieldsTypes'
import { FieldGroupId } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import { getFieldValue } from 'common/fields/models/CustomField/formatters'
import { getFormulaErrors } from 'common/fields/models/CustomField/validation'
import { IFlow } from 'common/flow/flowInterfaces'
import { IAdminInterface } from 'common/settings/interfaces'
import { formatDateTime } from 'common/usesList/utils'
import { parseVariableString } from 'components/MCTextEditor/variables/adapter'
import { VAR_REGEX } from 'constants/RegExs'
import { InstalledApplication } from 'shared/api/common/schemas/installedApplication'
import { Group } from 'shared/api/requests/groups/schemas'
import { Tag } from 'shared/api/requests/tags/schemas'

import { parseActionItem } from './adapter'
import { ActionTypes } from './constants'

type FormatActionValueOptions = Partial<{
  botTimezone: string | null
  emailInsteadOfMessengerEnabled: boolean
  html: boolean
  raw: boolean
}>

export interface FormatActionValueData {
  admins: IAdminInterface[]
  application: InstalledApplication | null
  events: Array<{ event_id: number; title: string; meta_type: number }>
  fields: Field[]
  flows: Record<string, IFlow>
  menus: IMenu[]
  sequences: Array<{ sequence_id: number; name: string }>
  tags: Tag[]
  zaps: Array<{ event_id: string; event_name: string }>
  autoAssignGroups: Group[]
  withGroups: boolean
}

interface Formatter<T extends Action> {
  (action: T, data: FormatActionValueData, options?: FormatActionValueOptions): string
}

export const formatActionValue = (
  action: Action,
  data: Partial<FormatActionValueData>,
  options: FormatActionValueOptions = {},
) => {
  if (!options.raw) {
    action = parseActionItem(action)
  }
  return escapeString(formatActionByType(action, prepareFormatData(data), options), options)
}

export const escapeString = (input: string, { html }: { html?: boolean }) => {
  if (html) {
    return escape(input)
      .replace(/\[MUTED\]/g, '<span class="text-secondary">')
      .replace(/\[ELLIPSIS\]/g, '')
      .replace(/\[DANGER\]/g, '<span class="text-danger">')
      .replace(/\[\/(MUTED)\]/g, '</span>')
      .replace(/\[\/(ELLIPSIS)\]/g, '<br />')
      .replace(/\[\/(DANGER)\]/g, '</span>')
  }

  return input
    .replace(/\[(MUTED|ELLIPSIS|DANGER)\]/g, '')
    .replace(/\[\/(MUTED|ELLIPSIS|DANGER)\]/g, '')
}

const prepareFormatData = (data: Partial<FormatActionValueData>): FormatActionValueData => ({
  admins: data.admins || [],
  application: data.application || null,
  events: data.events || [],
  fields: data.fields || [],
  flows: data.flows || {},
  menus: data.menus || [],
  sequences: data.sequences || [],
  tags: data.tags || [],
  zaps: data.zaps || [],
  autoAssignGroups: data.autoAssignGroups || [],
  withGroups: data.withGroups || false,
})

const formatActionByType = (
  action: Action,
  data: FormatActionValueData,
  options: FormatActionValueOptions,
) => {
  switch (action.type) {
    case ActionTypes.ADD_TAG:
    case ActionTypes.REMOVE_TAG:
      return formatTagAction(action, data)
    case ActionTypes.FIRE_CUSTOM_EVENT: {
      return formatCustomEventAction(action, data)
    }
    case ActionTypes.SUBSCRIBE:
    case ActionTypes.UNSUBSCRIBE:
      return formatSequenceAction(action, data)
    case ActionTypes.SET_CUF:
    case ActionTypes.SET_GAF: {
      return formatSetFieldAction(action, data, options)
    }
    case ActionTypes.UNSET_CUF:
      return formatUnsetFieldAction(action, data)
    case ActionTypes.EXTERNAL_REQUEST:
      return formatExternalRequestAction(action, data)
    case ActionTypes.NOTIFY_ADMINS: {
      return formatNotifyAdminsAction(action, data, options)
    }
    case ActionTypes.HUBSPOT_INTEGRATION: {
      return makeFormatIntegrationAction(HubSpotConfig)(action, data)
    }
    case ActionTypes.CONVERTKIT_INTEGRATION: {
      return makeFormatIntegrationAction(ConvertKitConfig)(action, data)
    }
    case ActionTypes.CHATGPT_INTEGRATION: {
      return makeFormatIntegrationAction(ChatGPTConfig)(action, data)
    }
    case ActionTypes.CLAUDE_INTEGRATION: {
      return makeFormatIntegrationAction(ClaudeConfig)(action, data)
    }
    case ActionTypes.DEEPSEEK_INTEGRATION: {
      return makeFormatIntegrationAction(DeepSeekConfig)(action, data)
    }
    case ActionTypes.GOOGLE_SHEETS_INTEGRATION: {
      return makeFormatIntegrationAction(GoogleSheetsConfig)(action, data)
    }
    case ActionTypes.ACTIVE_CAMPAIGN_INTEGRATION: {
      return makeFormatIntegrationAction(ActiveCampaignConfig)(action, data)
    }
    case ActionTypes.KLAVIYO: {
      return makeFormatIntegrationAction(KlaviyoConfig)(action, data)
    }
    case ActionTypes.MAILCHIMP_INTEGRATION: {
      return makeFormatIntegrationAction(MailChimpConfig)(action, data)
    }
    case ActionTypes.TRIGGER_ZAP:
      return formatTriggerZapAction(action, data)
    case ActionTypes.START_FLOW: {
      return formatStartFlowAction(action, data)
    }
    case ActionTypes.ASSIGN_CONVERSATION: {
      return formatAssignConversationAction(action, data)
    }
    case ActionTypes.CUSTOM_AUDIENCE_USER: {
      return formatCustomAudienceAction(action, data)
    }
    case ActionTypes.APPS_REQUEST: {
      return formatAppsRequestAction(action, data)
    }
    case ActionTypes.SET_MAIN_MENU: {
      return formatSetMainMenuAction(action, data)
    }
    case ActionTypes.PAUSE_AUTOMATIONS: {
      return formatPauseAllAutomationsAction(action, data)
    }
    default:
      return ''
  }
}

const formatSetMainMenuAction: Formatter<SetMainMenuAction> = (action, data) => {
  if (!action.main_menu_flow_ns) return l.translate('Select menu')

  const menu = data.menus?.find((menu) => menu.id === action.main_menu_flow_ns)
  return menu?.name ?? l.translate('Default Menu')
}

const formatAppsRequestAction: Formatter<AppsRequestAction> = (action, data) => {
  if (!data.application) return ''
  if (!action.action) return l.translate('Select action')

  const applicationAction = data.application.actions?.find((el) => el.name === action.action)
  return applicationAction?.title ?? l.translate('Action not found')
}

const formatCustomAudienceAction: Formatter<CustomAudienceAction> = (action) => {
  if (!action.user_id && !action.ad_account_id && !action.custom_audience_id && !action.action)
    return l.translate('Select action')

  return action.action === CustomAudienceActionType.remove
    ? l.translate('Remove Contact')
    : l.translate('Add Contact')
}

const formatAssignConversationAction: Formatter<AssignConversationAction> = (
  { user_id, group_id },
  data,
) => {
  if (data.withGroups && !user_id && !group_id) {
    return l.translate('Choose a team member or a group')
  }
  if (!data.withGroups && !user_id) {
    return l.translate('Choose a team member')
  }
  if (user_id) {
    const admin = data.admins.find((admin) => admin.user_id === user_id)
    return admin?.name ?? l.translate('Unknown user')
  }

  const group = data.autoAssignGroups.find((group) => group.group_id === group_id)
  return group?.name ?? l.translate('Unknown group')
}

const formatStartFlowAction: Formatter<StartFlowAction> = (action, data) => {
  if (!action.flow_ns) return l.translate('Choose Automation')

  return data.flows[action.flow_ns]?.name ?? '...'
}

const formatTriggerZapAction: Formatter<ZapAction> = (action, data) => {
  const zap = data.zaps.find((t) => t.event_id === action.event_id)
  return zap?.event_name ?? l.translate('Unknown event')
}

const formatNotifyAdminsAction: Formatter<NotifyAdminAction> = (action, data, options) => {
  const [adminLabel, channelLabel] = getNotifyAdminsLabels(action, {
    admins: data.admins,
    emailInsteadOfMessengerEnabled: options?.emailInsteadOfMessengerEnabled,
  })
  return `${adminLabel} [MUTED]${l.translate('via')}[/MUTED] ${channelLabel}`
}

const formatExternalRequestAction: Formatter<ExternalRequestAction> = ({ url }) => {
  return `${url || l.translate('Add your request')}`
}

const formatUnsetFieldAction: Formatter<FieldAction> = (action, data) => {
  const field = data.fields.find(makeFieldFind(action.field_id, FieldGroupId.USER))
  return field?.caption || l.translate('Unknown field')
}

const formatSetFieldAction: Formatter<FieldAction> = (action, data, options) => {
  const group = ActionTypes.SET_GAF === action.type ? FieldGroupId.BOT : FieldGroupId.USER
  const field = data.fields.find(makeFieldFind(action.field_id, group))
  if (!field) return `[DANGER]${l.translate('Unknown field')} (${action.field_id})[/DANGER]`

  const { caption, type } = field
  let [verb, preposition] = getCUFActionLabels(action)
  let value = getFieldValue(type, action.value, data.fields) || '[DANGER]Unknown[/DANGER]'

  if (
    [UserFieldsTypes.DATETIME, UserFieldsTypes.DATE].includes(type) &&
    dayjs(action.value).isValid()
  ) {
    verb = l.translate('Set')
    preposition = l.translate('to')
    const isDateOnly = type === UserFieldsTypes.DATE
    value = formatDateTime(action.value, isDateOnly, isDateOnly ? null : options?.botTimezone)
  }

  return caption
    ? `[MUTED]${verb}[/MUTED]${
        verb ? ' ' : ''
      }[ELLIPSIS]${caption}[/ELLIPSIS] [MUTED]${preposition}[/MUTED] ${value}`
    : l.translate('Please select custom field and value')
}

const formatSequenceAction: Formatter<SequenceAction> = (action, data) => {
  const sequence = data.sequences.find((s) => `${s.sequence_id}` === `${action.sequence_id}`)
  return sequence?.name ?? l.translate('Unknown sequence')
}

const formatTagAction: Formatter<SetTagAction> = (action, data) => {
  return data.tags.find((t) => t.tag_id === action.tag_id)?.tag_name ?? l.translate('Unknown tag')
}

const formatCustomEventAction: Formatter<CustomEventAction> = (action, data) => {
  const event = data.events.find((t) => t.event_id === action.event_id)
  if (!event) {
    return l.translate('Please select Conversion Event')
  }
  if (action.cost == null) {
    return `${event.title}`
  }
  const additionalInfo = '[MUTED]Set[/MUTED]'
  const preposition = '[MUTED]To[/MUTED]'
  let formulaErrors = []
  let cost = action.cost.toString().replace(VAR_REGEX, (el: string) => {
    const variableData = parseVariableString(el)
    const field = data.fields.find((f) => f.id === variableData?.id)
    return field?.caption ? `{${field.caption}}` : '[DANGER]Unknown[/DANGER]'
  })

  if (typeof action.cost === 'string') {
    formulaErrors = getFormulaErrors(action.cost)
    if (formulaErrors.length) {
      cost = '[DANGER]Incorrect Value[/DANGER]'
    }
  }
  let currencySign = ''
  const metaType = get(event, 'meta_type')
  if (metaType && formulaErrors.length === 0) {
    currencySign = formatMetaTypeCodeToCurrencySign(metaType)
  }
  return `${additionalInfo} ${event.title} ${preposition} ${cost} ${currencySign}`
}

const formatPauseAllAutomationsAction: Formatter<PauseAllAutomationsAction> = (action) => {
  const pauseDuration = action?.pause_duration ?? 0
  const pauseDurationLabel = l.getString(DelayAutomationsMap?.[pauseDuration] || '')
  return pauseDurationLabel || l.translate('Unknown amount of time')
}

const makeFormatIntegrationAction =
  (integrationConfig: Integration): Formatter<IntegrationAction> =>
  (action) => {
    const integrationAction = getIntegrationAction(integrationConfig, action.action)
    return integrationAction?.name || l.translate('Select action')
  }

const makeFieldFind = (id: number | undefined, group: string) => (field: Field) =>
  field.id === `${group}_${id}`
